import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { CommencementDateInput, DateInput, PageHeader, PageHeaderWithBack, } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { jobsService } from "@/api/services/jobs.service";
import { sitesService } from "@/api/services/sites.service";
import { checklistTemplatesService } from "@/api/services/checklist-templates.service";
import { contactsService } from "@/api/services/contacts.service";
import { isWeekend, toDateTime } from "@/utils/dateTime";
import { formatFullName, getContactDisplayName } from "@/utils/string";
import { formatSiteAddress } from "@/utils/address";
import styles from "./JobEdit.module.css";
const JOB_TYPE_OPTIONS = [
    { value: "full_defit", label: "Full Defit" },
    { value: "partial_defit", label: "Partial Defit" },
    { value: "make_good", label: "Make Good" },
    { value: "strip_out", label: "Strip Out" },
    { value: "demolition", label: "Demolition" },
];
export const AddJob = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { user } = useAuth();
    // Determine base path (admin or staff)
    const basePath = location.pathname.startsWith("/staff") ? "/staff" : "/admin";
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [saveMessage, setSaveMessage] = useState(null);
    const [sites, setSites] = useState([]);
    const [loadingSites, setLoadingSites] = useState(true);
    const [selectedSiteId, setSelectedSiteId] = useState("");
    const [checklistTemplates, setChecklistTemplates] = useState([]);
    // Company-first contact selection (like EstimatingEdit)
    const companyDropdownRef = useRef(null);
    const contactDropdownRef = useRef(null);
    const [companyNames, setCompanyNames] = useState([]);
    const [loadingCompanyNames, setLoadingCompanyNames] = useState(false);
    const [companySearchTerm, setCompanySearchTerm] = useState("");
    const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
    const [selectedCompanyName, setSelectedCompanyName] = useState("");
    const [companyContacts, setCompanyContacts] = useState([]);
    const [contactSearchTerm, setContactSearchTerm] = useState("");
    const [showContactDropdown, setShowContactDropdown] = useState(false);
    const [selectedContactDisplayName, setSelectedContactDisplayName] = useState("");
    // Site form (read-only, populated from selected site)
    const [siteForm, setSiteForm] = useState({
        siteName: "",
        address: "",
        suburb: "",
        state: "",
        postcode: "",
        buildingType: "",
        tenancyName: "",
        tenancyNumber: "",
        landlord: "",
        shoppingCenter: "",
    });
    // Job form state
    const [formData, setFormData] = useState({
        contactId: "",
        siteId: "",
        title: "",
        location: "",
        address: "",
        description: "",
        type: "full_defit",
        scope: "",
        commencementDate: "",
        completionDate: "",
        durationDays: 0,
        estimatedCost: undefined,
        notes: "",
        commencementTime: "",
        completionTime: "",
    });
    // Fetch company names for searchable dropdown
    const fetchCompanyNames = useCallback(async () => {
        try {
            setLoadingCompanyNames(true);
            const response = await contactsService.getCompaniesWithContactIds();
            const companiesData = response.data.data || [];
            const companyNameSet = new Set();
            companiesData.forEach((company) => {
                if (company.companyName && company.contactId) {
                    companyNameSet.add(company.companyName);
                }
            });
            const allCompanyNames = Array.from(companyNameSet);
            let filteredNames = allCompanyNames;
            if (companySearchTerm && companySearchTerm.trim()) {
                const searchLower = companySearchTerm.toLowerCase().trim();
                filteredNames = allCompanyNames.filter((name) => name.toLowerCase().includes(searchLower));
            }
            setCompanyNames(filteredNames);
        }
        catch (err) {
            console.error("Error fetching company names:", err);
            setCompanyNames([]);
        }
        finally {
            setLoadingCompanyNames(false);
        }
    }, [companySearchTerm]);
    // Handle company selection
    const handleCompanySelect = useCallback(async (companyName) => {
        setSelectedCompanyName(companyName);
        setCompanySearchTerm(companyName);
        setShowCompanyDropdown(false);
        setFormData((prev) => ({ ...prev, contactId: "" }));
        setContactSearchTerm("");
        setSelectedContactDisplayName("");
        setShowContactDropdown(false);
        try {
            const contactResponse = await contactsService.getContacts({
                companyName,
                page: 1,
            });
            const contacts = (contactResponse.data.data || []).map((contact) => ({
                contactId: contact.contactId,
                firstName: contact.firstName,
                lastName: contact.lastName,
                email: contact.email,
                phone: contact.phone,
                role: contact.role,
            }));
            setCompanyContacts(contacts);
        }
        catch (err) {
            console.error("Error fetching company contacts:", err);
            setCompanyContacts([]);
        }
    }, []);
    const handleClearCompany = useCallback(() => {
        setSelectedCompanyName("");
        setCompanySearchTerm("");
        setFormData((prev) => ({ ...prev, contactId: "" }));
        setContactSearchTerm("");
        setSelectedContactDisplayName("");
        setShowContactDropdown(false);
        setCompanyContacts([]);
    }, []);
    const handleContactSelect = useCallback((contact) => {
        const displayName = getContactDisplayName(contact.firstName, contact.lastName, contact.email);
        setFormData((prev) => ({ ...prev, contactId: contact.contactId }));
        setSelectedContactDisplayName(displayName);
        setContactSearchTerm(displayName);
        setShowContactDropdown(false);
    }, []);
    const handleClearContact = useCallback(() => {
        setFormData((prev) => ({ ...prev, contactId: "" }));
        setSelectedContactDisplayName("");
        setContactSearchTerm("");
        setShowContactDropdown(false);
    }, []);
    const filteredCompanyContacts = useMemo(() => {
        if (!selectedCompanyName)
            return [];
        if (!contactSearchTerm || !contactSearchTerm.trim()) {
            return companyContacts;
        }
        const searchLower = contactSearchTerm.toLowerCase().trim();
        return companyContacts.filter((contact) => {
            const fullName = formatFullName(contact.firstName, contact.lastName).toLowerCase();
            const email = (contact.email || "").toLowerCase();
            return fullName.includes(searchLower) || email.includes(searchLower);
        });
    }, [companyContacts, contactSearchTerm, selectedCompanyName]);
    // Load sites
    useEffect(() => {
        const fetchSites = async () => {
            try {
                const response = await sitesService.getSites({ pageSize: 100 });
                const siteList = response.data.data || [];
                setSites(siteList.map((s) => ({
                    id: s.id,
                    siteName: s.siteName,
                    address: s.address ?? null,
                    suburb: s.suburb ?? null,
                    state: s.state ?? null,
                    postcode: s.postcode ?? null,
                    buildingTypes: s.buildingTypes ?? null,
                    tenancyName: s.tenancyName ?? null,
                    tenancyNumber: s.tenancyNumber ?? null,
                    landlord: s.landlord ?? null,
                    shoppingCenter: s.shoppingCenter ?? s.contactPerson?.shoppingCenter ?? null,
                })));
            }
            catch (err) {
                console.error("Failed to load sites:", err);
            }
            finally {
                setLoadingSites(false);
            }
        };
        fetchSites();
    }, []);
    // Load job checklist templates for default items on job create
    useEffect(() => {
        const fetchChecklistTemplates = async () => {
            try {
                const response = await checklistTemplatesService.getChecklistTemplates(1, 100);
                setChecklistTemplates(response.data.data ?? []);
            }
            catch {
                setChecklistTemplates([]);
            }
        };
        fetchChecklistTemplates();
    }, []);
    // Handle site selection
    const handleSiteSelect = (siteId) => {
        setSelectedSiteId(siteId);
        setFormData((prev) => ({ ...prev, siteId }));
        if (!siteId) {
            setSiteForm({
                siteName: "",
                address: "",
                suburb: "",
                state: "",
                postcode: "",
                buildingType: "",
                tenancyName: "",
                tenancyNumber: "",
                landlord: "",
                shoppingCenter: "",
            });
            return;
        }
        const site = sites.find((s) => s.id === siteId);
        if (site) {
            setSiteForm({
                siteName: site.siteName || "",
                address: site.address || "",
                suburb: site.suburb || "",
                state: site.state || "",
                postcode: site.postcode || "",
                buildingType: site.buildingTypes?.[0] || "",
                tenancyName: site.tenancyName || "",
                tenancyNumber: site.tenancyNumber || "",
                landlord: site.landlord || "",
                shoppingCenter: site.shoppingCenter || "",
            });
            // Also set location from site
            setFormData((prev) => ({
                ...prev,
                location: site.suburb && site.state
                    ? `${site.suburb}, ${site.state}`
                    : prev.location,
                address: site.address || prev.address,
            }));
        }
    };
    // Pre-select site from URL parameter
    useEffect(() => {
        const siteIdParam = searchParams.get("siteId");
        if (siteIdParam && sites.length > 0 && selectedSiteId !== siteIdParam) {
            const siteExists = sites.some((s) => s.id === siteIdParam);
            if (siteExists) {
                handleSiteSelect(siteIdParam);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sites, searchParams]);
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };
    // Fetch company names when dropdown is shown
    useEffect(() => {
        if (showCompanyDropdown) {
            fetchCompanyNames();
        }
    }, [showCompanyDropdown, fetchCompanyNames]);
    // Handle click outside to close company dropdown
    useEffect(() => {
        if (!showCompanyDropdown)
            return;
        const container = companyDropdownRef.current;
        if (!container)
            return;
        const handleOutsideClick = (event) => {
            const target = event.target;
            if (container && target && !container.contains(target)) {
                setShowCompanyDropdown(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setShowCompanyDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        document.addEventListener("touchstart", handleOutsideClick);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.removeEventListener("touchstart", handleOutsideClick);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [showCompanyDropdown]);
    // Handle click outside to close contact dropdown
    useEffect(() => {
        if (!showContactDropdown)
            return;
        const container = contactDropdownRef.current;
        if (!container)
            return;
        const handleOutsideClick = (event) => {
            const target = event.target;
            if (container && target && !container.contains(target)) {
                setShowContactDropdown(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setShowContactDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        document.addEventListener("touchstart", handleOutsideClick);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
            document.removeEventListener("touchstart", handleOutsideClick);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [showContactDropdown]);
    const handleNumberChange = (name, value) => {
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };
    const handleSubmit = async () => {
        setError(null);
        // Validation
        if (!formData.contactId) {
            setError("Please select a contact");
            return;
        }
        if (!formData.siteId) {
            setError("Please select a site");
            return;
        }
        if (!formData.title) {
            setError("Please enter a job title");
            return;
        }
        if (formData.commencementDate && isWeekend(formData.commencementDate)) {
            setError("Commencement date cannot fall on a weekend.");
            return;
        }
        setLoading(true);
        try {
            // Derive location/address from site if not set (kept internally, not displayed)
            const site = sites.find((s) => s.id === formData.siteId);
            const location = formData.location ||
                (site?.suburb && site?.state
                    ? `${site.suburb}, ${site.state}`
                    : site?.address || "");
            const address = formData.address || site?.address || undefined;
            // Clean up formData - remove empty strings for optional fields
            const cleanedData = {
                contactId: formData.contactId,
                siteId: formData.siteId,
                title: formData.title,
                location,
                type: formData.type,
                status: "new_project",
                ...(address && { address }),
                ...(formData.description && { description: formData.description }),
                ...(formData.scope && { scope: formData.scope }),
                ...(formData.commencementDate && {
                    commencementDate: toDateTime(formData.commencementDate, formData.commencementTime || ""),
                }),
                ...(formData.completionDate && {
                    completionDate: toDateTime(formData.completionDate, formData.completionTime || ""),
                }),
                ...(formData.durationDays && { durationDays: formData.durationDays }),
                ...(formData.estimatedCost && {
                    estimatedCost: formData.estimatedCost,
                }),
                ...(formData.notes && { notes: formData.notes }),
            };
            const response = await jobsService.createJob(cleanedData);
            const jobId = response.data.id;
            if (checklistTemplates.length > 0) {
                for (const template of checklistTemplates) {
                    try {
                        await jobsService.createJobChecklistItem({
                            jobId,
                            description: template.description,
                        });
                    }
                    catch {
                        // Continue with other items; job is already created
                    }
                }
            }
            setSaveMessage("Job created successfully!");
            setTimeout(() => {
                navigate(`${basePath}/jobs/${jobId}/edit`);
            }, 1000);
        }
        catch (err) {
            console.error("Failed to create job:", err);
            setError(err.response?.data?.message || "Failed to create job");
            setLoading(false);
        }
    };
    return (_jsxs("div", { className: styles.container, children: [_jsx(PageHeader, { userName: user?.name || "User", userEmail: user?.email || "" }), (saveMessage || error) && (_jsx("div", { className: `${styles.actionMessage} ${error ? styles.error : styles.success}`, children: saveMessage || error })), _jsx(PageHeaderWithBack, { title: "Create Job", onBack: () => navigate(-1) }), _jsx("div", { className: styles.contentArea, children: _jsxs("div", { className: styles.detailContainer, children: [_jsxs("section", { className: styles.section, children: [_jsx("h2", { className: styles.sectionTitle, children: "CONTACT" }), _jsxs("div", { className: styles.card, children: [_jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsxs("label", { className: "form-label", children: ["Select Company ", _jsx("span", { className: styles.required, children: "*" })] }), _jsxs("div", { className: styles.searchableDropdown, ref: companyDropdownRef, children: [_jsxs("div", { className: styles.searchInputWrapper, children: [_jsx("input", { type: "text", className: styles.searchInput, placeholder: "Type here to search", value: companySearchTerm, onChange: (e) => {
                                                                        setCompanySearchTerm(e.target.value);
                                                                        if (!showCompanyDropdown)
                                                                            setShowCompanyDropdown(true);
                                                                    }, onFocus: () => setShowCompanyDropdown(true) }), selectedCompanyName && (_jsx("button", { type: "button", className: styles.clearButton, onClick: handleClearCompany, title: "Clear selection", children: "\u00D7" }))] }), showCompanyDropdown && (_jsx("div", { className: styles.dropdownList, children: loadingCompanyNames ? (_jsx("div", { className: styles.dropdownLoading, children: "Loading..." })) : companyNames.length > 0 ? (companyNames.slice(0, 50).map((name) => (_jsx("div", { className: styles.dropdownItem, onClick: () => handleCompanySelect(name), children: name }, name)))) : (_jsx("div", { className: styles.dropdownEmpty, children: companySearchTerm
                                                                    ? "No companies found"
                                                                    : "Type to search..." })) }))] })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsxs("label", { className: "form-label", children: ["Select Contact ", _jsx("span", { className: styles.required, children: "*" })] }), _jsxs("div", { className: styles.searchableDropdown, ref: contactDropdownRef, children: [_jsxs("div", { className: styles.searchInputWrapper, children: [_jsx("input", { type: "text", className: styles.searchInput, placeholder: !selectedCompanyName
                                                                        ? "Select a company first"
                                                                        : "Type here to search", value: contactSearchTerm, onChange: (e) => {
                                                                        setContactSearchTerm(e.target.value);
                                                                        if (!showContactDropdown)
                                                                            setShowContactDropdown(true);
                                                                    }, onFocus: () => {
                                                                        if (selectedCompanyName) {
                                                                            setShowContactDropdown(true);
                                                                        }
                                                                    }, disabled: !selectedCompanyName }), selectedContactDisplayName && (_jsx("button", { type: "button", className: styles.clearButton, onClick: handleClearContact, title: "Clear selection", children: "\u00D7" }))] }), showContactDropdown && selectedCompanyName && (_jsx("div", { className: styles.dropdownList, children: filteredCompanyContacts.length > 0 ? (filteredCompanyContacts.map((contact) => (_jsxs("div", { className: styles.dropdownItem, onClick: () => handleContactSelect(contact), children: [_jsx("div", { style: { fontWeight: "500" }, children: formatFullName(contact.firstName, contact.lastName) }), contact.email && (_jsx("div", { style: {
                                                                            fontSize: "0.85em",
                                                                            color: "#666",
                                                                        }, children: contact.email }))] }, contact.contactId)))) : (_jsx("div", { className: styles.dropdownEmpty, children: contactSearchTerm
                                                                    ? "No contacts found"
                                                                    : "No contacts available" })) }))] })] })] })] }), _jsxs("section", { className: styles.section, children: [_jsx("h2", { className: styles.sectionTitle, children: "SITE DETAILS" }), _jsxs("div", { className: styles.siteSelect, children: [_jsxs("label", { className: "form-label", children: ["Select Site ", _jsx("span", { className: styles.required, children: "*" })] }), _jsxs("div", { className: styles.siteSelectContainer, children: [_jsxs("select", { className: "form-select", value: selectedSiteId, onChange: (e) => handleSiteSelect(e.target.value), disabled: loadingSites, children: [_jsx("option", { value: "", children: loadingSites ? "Loading sites..." : "Select a Site" }), sites.map((site) => (_jsxs("option", { value: site.id, children: [site.siteName, " - ", formatSiteAddress(site, "No address")] }, site.id)))] }), _jsx("button", { type: "button", className: styles.addSiteButton, onClick: () => {
                                                        const currentPath = `${basePath}/jobs/add`;
                                                        navigate(`${basePath}/sites/create?returnUrl=${encodeURIComponent(currentPath)}`);
                                                    }, children: "Add New Site" })] })] }), _jsx("div", { className: styles.card, children: _jsxs("div", { className: styles.siteGrid, children: [_jsxs("div", { className: styles.siteColumn, children: [_jsxs("div", { className: `${styles.formGroupInline} form-group-row`, children: [_jsx("label", { className: styles.label, children: "Site Name" }), _jsx("input", { type: "text", className: styles.input, value: siteForm.siteName, readOnly: true })] }), _jsxs("div", { className: `${styles.formGroupInline} form-group-row`, children: [_jsx("label", { className: styles.label, children: "Address" }), _jsx("input", { type: "text", className: styles.input, value: siteForm.address, readOnly: true })] }), _jsxs("div", { className: `${styles.formGroupInline} form-group-row`, children: [_jsx("label", { className: styles.label, children: "Suburb" }), _jsx("input", { type: "text", className: styles.input, value: siteForm.suburb, readOnly: true })] })] }), _jsxs("div", { className: styles.siteColumn, children: [_jsxs("div", { className: `${styles.formGroupInline} form-group-row`, children: [_jsx("label", { className: styles.label, children: "State" }), _jsx("input", { type: "text", className: styles.input, value: siteForm.state, readOnly: true })] }), _jsxs("div", { className: `${styles.formGroupInline} form-group-row`, children: [_jsx("label", { className: styles.label, children: "Postcode" }), _jsx("input", { type: "text", className: styles.input, value: siteForm.postcode, readOnly: true })] }), _jsxs("div", { className: `${styles.formGroupInline} form-group-row`, children: [_jsx("label", { className: styles.label, children: "Building Type" }), _jsx("input", { type: "text", className: styles.input, value: siteForm.buildingType, readOnly: true })] })] }), _jsxs("div", { className: styles.siteColumn, children: [_jsxs("div", { className: `${styles.formGroupInline} form-group-row`, children: [_jsx("label", { className: styles.label, children: "Tenancy Name" }), _jsx("input", { type: "text", className: styles.input, value: siteForm.tenancyName, readOnly: true })] }), _jsxs("div", { className: `${styles.formGroupInline} form-group-row`, children: [_jsx("label", { className: styles.label, children: "Tenancy Number" }), _jsx("input", { type: "text", className: styles.input, value: siteForm.tenancyNumber, readOnly: true })] }), _jsxs("div", { className: `${styles.formGroupInline} form-group-row`, children: [_jsx("label", { className: styles.label, children: "Landlord" }), _jsx("input", { type: "text", className: styles.input, value: siteForm.landlord, readOnly: true })] })] })] }) })] }), _jsxs("section", { className: styles.section, children: [_jsx("h2", { className: styles.sectionTitle, children: "JOB DETAILS" }), _jsx("div", { className: styles.card, children: _jsxs("div", { className: styles.scopeGrid, children: [_jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsxs("label", { className: "form-label", children: ["Job Title ", _jsx("span", { className: styles.required, children: "*" })] }), _jsx("input", { type: "text", name: "title", className: "form-input", value: formData.title, onChange: handleInputChange })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsxs("label", { className: "form-label", children: ["Job Type ", _jsx("span", { className: styles.required, children: "*" })] }), _jsx("select", { name: "type", value: formData.type, onChange: handleInputChange, className: "form-select", children: JOB_TYPE_OPTIONS.map((option) => (_jsx("option", { value: option.value, children: option.label }, option.value))) })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Shopping Centre" }), _jsx("input", { type: "text", className: "form-input", value: siteForm.shoppingCenter, readOnly: true })] })] }) })] }), _jsxs("section", { className: styles.section, children: [_jsx("h2", { className: styles.sectionTitle, children: "JOB SCOPE" }), _jsx("div", { className: styles.card, children: _jsxs("div", { className: styles.scopeGrid, children: [_jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Job Scope" }), _jsx("textarea", { name: "scope", className: "form-textarea", rows: 8, value: formData.scope, onChange: handleInputChange })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row`, children: [_jsx("label", { className: "form-label", children: "Job Description" }), _jsx("textarea", { name: "description", className: "form-textarea", rows: 8, value: formData.description, onChange: handleInputChange })] })] }) })] }), _jsxs("section", { className: styles.section, children: [_jsx("h2", { className: styles.sectionTitle, children: "JOB DATES" }), _jsxs("div", { className: styles.card, children: [_jsxs("div", { className: `${styles.formGroup} form-group-row ${styles.inlineField}`, children: [_jsx("label", { className: `form-label ${styles.inlineLabel}`, children: "Duration" }), _jsxs("div", { className: styles.durationGroup, children: [_jsx("input", { type: "number", className: styles.durationInput, value: formData.durationDays || 0, onChange: (e) => handleNumberChange("durationDays", parseInt(e.target.value) || 0) }), _jsx("span", { className: styles.durationLabel, children: "(Days)" })] })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row ${styles.inlineField}`, children: [_jsx("label", { className: `form-label ${styles.inlineLabel}`, children: "Commencement Date" }), _jsxs("div", { className: styles.dateInputWrapper, children: [_jsx("div", { className: styles.dateInput, children: _jsx(CommencementDateInput, { name: "commencementDate", className: "form-input", value: formData.commencementDate ?? "", onChange: (value) => setFormData((prev) => ({
                                                                    ...prev,
                                                                    commencementDate: value,
                                                                })) }) }), _jsx("div", { className: styles.timeInput, children: _jsx("input", { type: "time", name: "commencementTime", className: "form-input", value: formData.commencementTime, onChange: handleInputChange }) })] })] }), _jsxs("div", { className: `${styles.formGroup} form-group-row ${styles.inlineField}`, children: [_jsx("label", { className: `form-label ${styles.inlineLabel}`, children: "Completion Date" }), _jsxs("div", { className: styles.dateInputWrapper, children: [_jsx("div", { className: styles.dateInput, children: _jsx(DateInput, { name: "completionDate", className: "form-input", value: formData.completionDate ?? "", onChange: (value) => setFormData((prev) => ({ ...prev, completionDate: value })) }) }), _jsx("div", { className: styles.timeInput, children: _jsx("input", { type: "time", name: "completionTime", className: "form-input", value: formData.completionTime, onChange: handleInputChange }) })] })] })] })] }), _jsxs("div", { className: styles.formActions, children: [_jsx("button", { className: styles.cancelBtn, type: "button", onClick: () => navigate(-1), disabled: loading, children: "Cancel" }), _jsx("button", { className: styles.saveButton, type: "button", onClick: handleSubmit, disabled: loading, children: loading ? "Creating..." : "Create Job" })] })] }) })] }));
};
